import {
  DIRECTED_CLIP_SEC,
  DIRECTED_MIN_SHOT_SEC,
  directedClipKeys,
  directedClipSecOf,
  type DirectedScript,
  type DirectedShot,
  type DirectedVideoClipEntry,
} from 'fym-shared/src/music24/directedVideoApi';
import { MUSIC_VIDEO_EFFECTS, type MusicVideoEffect } from 'fym-shared/src/music24/musicVideoEffects';

/** Seconds kept to the millisecond, as the shared parse stores them. */
function roundSec(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** The number of each clip (1, 2, …) in the order of its first shot: "Clip 3". Pure. */
export function clipNumbers(script: DirectedScript): Map<string, number> {
  return new Map(directedClipKeys(script).map((key, index) => [key, index + 1]));
}

/** True when the shot is the first shot of its clip: it starts a new AI shot of the price. Pure. */
export function isClipStart(script: DirectedScript, shotId: string): boolean {
  const shot = script.shots.find((item) => item.id === shotId);
  return shot !== undefined && script.shots.find((item) => item.clipKey === shot.clipKey)?.id === shotId;
}

/** True when no other shot uses the clip of the shot: a removal makes the price smaller. Pure. */
export function hasOwnClip(script: DirectedScript, shotId: string): boolean {
  const shot = script.shots.find((item) => item.id === shotId);
  return shot !== undefined && script.shots.every((item) => item.id === shotId || item.clipKey !== shot.clipKey);
}

/**
 * The length of the clip of a shot (`clipSec`). A shot with no length (a script from before
 * 2026-10-23) reads as 8 s. Pure.
 */
export function clipSecOfShot(shot: Pick<DirectedShot, 'clipSec'>): number {
  return directedClipSecOf(shot.clipSec);
}

/**
 * The length of a new clip for a shot of `lengthSec`: whole seconds, at least the shot, from 3 to
 * 15 s. Pure.
 */
export function newClipSecFor(lengthSec: number): number {
  return directedClipSecOf(Math.ceil(roundSec(lengthSec) - 1e-9));
}

/** The script with one shot replaced (same id). Pure. */
export function replaceShot(script: DirectedScript, shot: DirectedShot): DirectedScript {
  return { ...script, shots: script.shots.map((item) => (item.id === shot.id ? shot : item)) };
}

export interface ShotRemoval {
  script: DirectedScript;
  /** The neighbour that takes the time of the removed shot, and its new times. Null: the time stays empty. */
  grown: { shotId: string; startSec: number; endSec: number; side: 'previous' | 'next' } | null;
}

/**
 * Removes a shot (design e2): the shot before it grows into its time, as far as its clip
 * (`clipSec`) lets it; with no shot before it, the shot after it starts earlier. A time the
 * neighbour cannot fill stays empty (the cut builder covers it). Null for the only shot of the
 * script. Pure.
 */
export function removeShot(script: DirectedScript, shotId: string): ShotRemoval | null {
  const index = script.shots.findIndex((shot) => shot.id === shotId);
  const removed = script.shots[index];
  if (removed === undefined || script.shots.length <= 1) {
    return null;
  }
  const shots = script.shots.filter((shot) => shot.id !== shotId);
  const previous = script.shots[index - 1];
  const next = script.shots[index + 1];
  let grown: ShotRemoval['grown'] = null;
  if (previous !== undefined) {
    const endSec = roundSec(
      Math.max(
        previous.endSec,
        Math.min(removed.endSec, previous.startSec + clipSecOfShot(previous) - previous.clipInSec)
      )
    );
    grown = { shotId: previous.id, startSec: previous.startSec, endSec, side: 'previous' };
  } else if (next !== undefined) {
    const startSec = roundSec(
      Math.min(
        next.startSec,
        Math.max(removed.startSec, next.endSec - clipSecOfShot(next), next.startSec - next.clipInSec)
      )
    );
    grown = { shotId: next.id, startSec, endSec: next.endSec, side: 'next' };
  }
  const result = grown;
  return {
    grown: result,
    script: {
      ...script,
      shots:
        result === null
          ? shots
          : shots.map((shot) =>
              shot.id === result.shotId
                ? {
                    ...shot,
                    startSec: result.startSec,
                    endSec: result.endSec,
                    clipInSec: roundSec(Math.max(0, shot.clipInSec - (shot.startSec - result.startSec))),
                  }
                : shot
            ),
    },
  };
}

/** A new id that no shot or clip of the script has. */
export function newScriptId(script: DirectedScript, prefix: 'shot' | 'clip'): string {
  const used = new Set(script.shots.flatMap((shot) => [shot.id, shot.clipKey]));
  let number = script.shots.length + 1;
  while (used.has(`${prefix}-new-${number}`)) {
    number += 1;
  }
  return `${prefix}-new-${number}`;
}

export interface ShotInsert {
  script: DirectedScript;
  shot: DirectedShot;
}

/**
 * "+ Add a shot here" (design e1): a new shot after `afterId`, with its own new clip. It takes the
 * empty time after the shot when there is at least 1 s of it (at most 8 s), else the second half of
 * the shot. The new clip is as long as the shot, in whole seconds (at least 3 s). The new shot has
 * no text yet. Null when the shot is too short to split. Pure.
 */
export function insertShotAfter(script: DirectedScript, afterId: string): ShotInsert | null {
  const index = script.shots.findIndex((shot) => shot.id === afterId);
  const after = script.shots[index];
  if (after === undefined) {
    return null;
  }
  const following = script.shots[index + 1];
  const base = {
    id: newScriptId(script, 'shot'),
    scene: '',
    veoPrompt: '',
    castIds: [...after.castIds],
    camera: after.camera,
    clipKey: newScriptId(script, 'clip'),
    clipInSec: 0,
  };
  const gapEnd = following?.startSec ?? after.endSec;
  if (gapEnd - after.endSec >= 1) {
    // A shot that the artist adds is at most 8 s long (6 credits). Only the director picks a longer clip.
    const endSec = roundSec(Math.min(gapEnd, after.endSec + DIRECTED_CLIP_SEC));
    const shot = { ...base, startSec: after.endSec, endSec, clipSec: newClipSecFor(endSec - after.endSec) };
    const shots = [...script.shots];
    shots.splice(index + 1, 0, shot);
    return { script: { ...script, shots }, shot };
  }
  if (after.endSec - after.startSec < DIRECTED_MIN_SHOT_SEC * 2) {
    return null;
  }
  const middle = roundSec((after.startSec + after.endSec) / 2);
  const shot = { ...base, startSec: middle, endSec: after.endSec, clipSec: newClipSecFor(after.endSec - middle) };
  const shots = script.shots.map((item) => (item.id === afterId ? { ...item, endSec: middle } : item));
  shots.splice(index + 1, 0, shot);
  return { script: { ...script, shots }, shot };
}

/** The effects that lie over the time of a shot. Pure. */
export function effectsOfShot(
  script: DirectedScript,
  shot: Pick<DirectedShot, 'startSec' | 'endSec'>
): MusicVideoEffect[] {
  return script.effects.filter((effect) => effect.startSec < shot.endSec && effect.endSec > shot.startSec);
}

/** The colour group of an effect on the strip and the chips: one colour per effect family. */
export type EffectTone = 'title' | 'transition' | 'lyric' | 'shot';

/** The tones in the order of the strip legend. */
export const EFFECT_TONES: readonly EffectTone[] = ['title', 'lyric', 'transition', 'shot'];

export function effectToneOf(effect: Pick<MusicVideoEffect, 'kind'>): EffectTone {
  return MUSIC_VIDEO_EFFECTS[effect.kind].family;
}

export interface ScriptFacts {
  shots: number;
  clips: number;
  /** Shots that use a part of a clip of another shot (no new shot). */
  reused: number;
  effects: number;
  lyricMoments: number;
  /** The end of the last shot. */
  endSec: number;
}

/** The numbers of the side panel (design e1). Pure. */
export function scriptFacts(script: DirectedScript): ScriptFacts {
  const clips = directedClipKeys(script).length;
  return {
    shots: script.shots.length,
    clips,
    reused: script.shots.length - clips,
    effects: script.effects.length,
    lyricMoments: script.effects.filter((effect) => effectToneOf(effect) === 'lyric').length,
    endSec: script.shots.reduce((end, shot) => Math.max(end, shot.endSec), 0),
  };
}

/** The text fields of a shot that a stale save keeps in a draft (design e2). */
export type ShotText = Pick<DirectedShot, 'scene' | 'camera' | 'veoPrompt' | 'castIds'>;

export function shotTextOf(shot: DirectedShot): ShotText {
  return { scene: shot.scene, camera: shot.camera, veoPrompt: shot.veoPrompt, castIds: [...shot.castIds] };
}

/**
 * The shot as the save sends it: trimmed text, and the prompt of a new shot from its text when the
 * artist wrote no prompt. Pure.
 */
export function shotForSave(shot: DirectedShot): DirectedShot {
  const scene = shot.scene.trim();
  const veoPrompt = shot.veoPrompt.trim();
  return { ...shot, scene, camera: shot.camera.trim(), veoPrompt: veoPrompt === '' ? scene : veoPrompt };
}

/**
 * The still of each made clip, by `clipKey` (the make sends `clipEntries`; empty before it). A clip
 * with no still (queued, making, failed, no poster) has no entry: its shots show the placeholder. Pure.
 */
export function clipStillsOf(entries: readonly DirectedVideoClipEntry[] | undefined): Map<string, string> {
  const stills = new Map<string, string>();
  for (const entry of entries ?? []) {
    if (entry.stillUrl !== null && entry.stillUrl !== '') {
      stills.set(entry.clipKey, entry.stillUrl);
    }
  }
  return stills;
}
